import React, { useRef, useState, useEffect, useMemo } from 'react'
import L from 'leaflet'
import {
    Map,
    TileLayer,
    Marker,
    FeatureGroup,
    ZoomControl,
    FeatureGroupProps,
    MapProps,
    ScaleControl,
    Rectangle
} from 'react-leaflet'
import MarkerClusterGroup from 'react-leaflet-markercluster'
import _ from 'lodash'
import { MapWidgetEvent, WidgetState } from '../api'
import CenterButton from '../CenterButton'
import SearchBar from '../SearchBar'
import Tooltip, { TooltipStyles } from '../ToolTip'
import { getMarkerIcon, markerStyles } from '../Markers'
import styled, { css } from 'styled-components'
import { widgetPadding, webBtnSize, mobileBtnSize } from '../constants'
import '../assets/widget-icon.css'
import '../assets/leaflet.css'
import '../assets/leaflet-markercluster.css'
import UnselectAllAreas from '../UnselectAllAreas'

export interface SelectedAsset {
    name: string
    target: L.Layer
}

const MapWidget = ({
    meta,
    batch,
    width,
    height,
    dashTheme,
    isMobile
}: {
    batch: Array<MapWidgetEvent>
    meta: WidgetState['meta']
    width?: number
    height?: number
    dashTheme?: string
    isMobile?: string
}): React.ReactElement => {
    const mapRef = useRef<Map<MapProps, L.Map>>(null)
    const groupRef = useRef<FeatureGroup<FeatureGroupProps, L.FeatureGroup>>(null)
    const [searchValue, setSearchValue] = useState<string>('')
    const [showAreas, setShowAreas] = useState<Array<number>>([])
    const [highlightAsset, setHighlightAsset] = useState<string>('')
    const [selectedAsset, setSelectedAsset] = useState<SelectedAsset>(null)
    const minZoom = useMemo(() => Math.ceil(Math.log2(Math.max(width, height) / 256)), [width, height])

    const clusterizeAssets = meta?.clusterize !== false // null, undefined or true will clusterize the assets
    const reRenderDebounce = _.debounce(() => mapRef.current.leafletElement.invalidateSize(), 1000)

    function centerMap(): void {
        try {
            if (batch.length > 1) {
                const groupBounds = groupRef.current ? groupRef.current.leafletElement.getBounds() : null

                if (groupBounds) {
                    mapRef.current.leafletElement.fitBounds(groupBounds.pad(meta?.initialZoomOut ?? 0), {
                        padding: [20, 20]
                    })
                }
            } else if (batch.length === 1) {
                const first = batch[0]

                if (first.lat && first.long) {
                    mapRef.current.leafletElement.setView(L.latLng(parseFloat(first.lat), parseFloat(first.long)), 4)
                }

                if (first.bbox) {
                    mapRef.current.leafletElement.setView(
                        L.latLng(parseFloat(first.bbox[0][0]), parseFloat(first.bbox[0][1])),
                        4
                    )
                }
            }
        } catch (e) {
            console.error('Error centering map')
            throw e
        }
    }

    function cleanSelectedAsset(cleanSearch?: boolean): void {
        if (cleanSearch) setSearchValue('')
        if (!_.isNull(selectedAsset)) {
            selectedAsset.target.closePopup()
            setSelectedAsset(null)
        }
    }

    function selectAsset(assetName: string, animationType: 'panTo' | 'flyTo' = 'panTo', e?: L.LeafletMouseEvent): void {
        if (!assetName) return

        const assetEvent = batch.find(event => event.asset === assetName)

        const target = e
            ? e.target
            : groupRef.current.leafletElement
                  .getLayers()
                  .find(marker => _.get(marker, 'options.id', null) === assetName)

        setSearchValue(assetName)
        setSelectedAsset({ name: assetName, target: target })

        target.openPopup()

        const assetlocation = L.latLng(parseFloat(assetEvent.lat), parseFloat(assetEvent.long))
        if (animationType === 'panTo') {
            mapRef.current.leafletElement.panTo(assetlocation)
        } else if (animationType === 'flyTo') {
            mapRef.current.leafletElement.flyTo(assetlocation, 9)
        }
    }

    function selectMarker(
        assetName: string,
        animationType: 'panTo' | 'flyTo' = 'panTo',
        includeBoundingArea: boolean,
        areaKey: number,
        e?: L.LeafletMouseEvent
    ): void {
        if (includeBoundingArea) {
            setShowArea(areaKey)
        }
        selectAsset(assetName, animationType, e)
    }

    function setShowArea(areaKey: number, value?: number): void {
        if (value == undefined) {
            value = showAreas[areaKey] === 1 ? 0 : 1
        }
        showAreas[areaKey] = value
    }

    function showAllAreas(value: number): void {
        showAreas.map((area, key) => setShowArea(key, value))
    }

    useEffect(() => {
        reRenderDebounce()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [height, width])

    const groupedAssets = (
        <GroupedAssets
            batch={batch}
            showAreas={showAreas}
            searchValue={searchValue}
            highlightAsset={highlightAsset}
            selectedAsset={selectedAsset}
            isMobile={isMobile}
            selectAsset={selectAsset}
            selectMarker={selectMarker}
            cleanSelectedAsset={cleanSelectedAsset}
        />
    )

    const featureGroup = clusterizeAssets ? (
        <MarkerClusterGroup
            maxClusterRadius={60}
            spiderfyOnMaxZoom={false}
            showCoverageOnHover={false}
            zoomToBoundsOnClick={false}
            onClusterClick={e => {
                e.layer.spiderfy()
            }}
        >
            {groupedAssets}
        </MarkerClusterGroup>
    ) : (
        groupedAssets
    )

    return (
        <HighOrderDivMap dashTheme={dashTheme} isMobile={isMobile}>
            <Map
                closePopupOnClick={false}
                zoomControl={false}
                ref={mapRef}
                style={MapStyle}
                zoom={0}
                center={[0, 0]}
                whenReady={(): void => centerMap()}
                dragging={!isMobile}
                tap={!isMobile}
                minZoom={minZoom}
                onclick={(): void => {
                    if (selectedAsset) cleanSelectedAsset(true)
                }}
                maxBounds={L.latLngBounds(L.latLng(-90, -180), L.latLng(90, 180))}
                worldCopyJump={true}
                onzoomend={(): void => {
                    const selectedAssetTarget = _.get(selectedAsset, 'target', null)
                    if (selectedAssetTarget) {
                        setTimeout(() => {
                            selectedAssetTarget.openPopup()
                        }, 500)
                    }
                }}
            >
                <TileLayer
                    url={
                        dashTheme === 'light'
                            ? 'http://{s}.tile.osm.org/{z}/{x}/{y}.png'
                            : 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
                    }
                    noWrap
                />
                <TileLayer
                    url={
                        dashTheme === 'light'
                            ? 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
                            : 'https://maps.heigit.org/openmapsurfer/tiles/hybrid/webmercator/{z}/{x}/{y}.png'
                    }
                    attribution={`${
                        dashTheme === 'light' ? '' : '&copy; CARTO'
                    } &copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors`}
                    noWrap
                />
                <ZoomControl position="bottomright" />
                <ScaleControl position="bottomleft" />

                <FeatureGroup ref={groupRef}>
                    {featureGroup}
                </FeatureGroup>
            </Map>
            <SearchBar
                options={batch}
                searchValue={searchValue}
                setSearchValue={setSearchValue}
                selectAsset={selectAsset}
                cleanSelectedAsset={cleanSelectedAsset}
                setHighlightAsset={setHighlightAsset}
                dashTheme={dashTheme}
                isMobile={isMobile}
            />
            <UnselectAllAreas
                options={batch}
                showAllAreas={value => showAllAreas(value)}
                cleanSelectedAsset={cleanSelectedAsset}
                dashTheme={dashTheme}
                isMobile={isMobile}
            />
            <CenterButton onClick={centerMap} dashTheme={dashTheme} isMobile={isMobile} />
        </HighOrderDivMap>
    )
}

export default MapWidget

const GroupedAssets = ({
    batch,
    searchValue,
    highlightAsset,
    selectedAsset,
    isMobile,
    showAreas,
    selectAsset,
    selectMarker,
    cleanSelectedAsset
}: {
    batch: Array<MapWidgetEvent>
    searchValue: String
    highlightAsset: String
    selectedAsset: SelectedAsset
    isMobile: String
    showAreas: Array<number>
    selectAsset(assetName: string, animationType: 'panTo' | 'flyTo', e?: L.LeafletMouseEvent): void
    selectMarker: (
        assetName: string,
        animationType: 'panTo' | 'flyTo',
        includeBoundingArea: boolean,
        areaKey: React.Key,
        e?: L.LeafletMouseEvent
    ) => void
    cleanSelectedAsset: (cleanSearch?: boolean) => void
}): React.ReactElement => {
    return (
        <>
            {batch.map((assetEvent, key) => (
                <Asset
                    areaKey={key}
                    showArea={showAreas[key] == undefined ? 0 : showAreas[key]}
                    assetEvent={assetEvent}
                    searchValue={searchValue}
                    highlightAsset={highlightAsset}
                    selectedAsset={selectedAsset}
                    isMobile={isMobile}
                    selectAsset={selectAsset}
                    selectMarker={selectMarker}
                    cleanSelectedAsset={cleanSelectedAsset}
                />
            ))}
        </>
    )
}

const Asset = ({
    areaKey,
    assetEvent,
    searchValue,
    highlightAsset,
    selectedAsset,
    isMobile,
    showArea,
    selectAsset,
    selectMarker,
    cleanSelectedAsset
}: {
    areaKey: React.Key
    assetEvent: MapWidgetEvent
    searchValue: String
    highlightAsset: String
    selectedAsset: SelectedAsset
    isMobile: String
    showArea: number
    selectAsset(assetName: string, animationType: 'panTo' | 'flyTo', e?: L.LeafletMouseEvent): void
    selectMarker: (
        assetName: string,
        animationType: 'panTo' | 'flyTo',
        includeBoundingArea: boolean,
        areaKey: React.Key,
        e?: L.LeafletMouseEvent
    ) => void
    cleanSelectedAsset: (cleanSearch?: boolean) => void
}): React.ReactElement => {
    const includeRectangle = assetEvent.asset_type === 'area'
    const willIncludeMarker = assetEvent.lat != null && assetEvent.long != null
    const rectangleTooltip = willIncludeMarker ? null : <Tooltip event={assetEvent} onClose={cleanSelectedAsset} />

    return (
        <FeatureGroup>
            {includeRectangle ? (
                <Rectangle
                    key={areaKey}
                    opacity={showArea}
                    fillColor={'none'}
                    color={assetEvent.color ?? 'gray'}
                    bounds={assetEvent.bbox}
                    onMouseOver={(ev: L.LeafletMouseEvent): void => {
                        if (isMobile) {
                            selectAsset(assetEvent.asset, 'panTo', ev)
                        } else if (!selectedAsset) {
                            ev.target.openPopup()
                        }
                    }}
                    onMouseOut={(ev: L.LeafletMouseEvent): void => selectedAsset && ev.target.closePopup()}
                    onClick={(ev: L.LeafletMouseEvent): void => selectAsset(assetEvent.asset, 'panTo', ev)}
                >
                    {rectangleTooltip}
                </Rectangle>
            ) : null}
            {willIncludeMarker ? (
                <CustomMarker
                    assetEvent={assetEvent}
                    markerKey={assetEvent.asset}
                    areaKey={areaKey}
                    searchValue={searchValue}
                    highlightAsset={highlightAsset}
                    selectedAsset={selectedAsset}
                    isMobile={isMobile}
                    includeBoundingArea={includeRectangle}
                    selectMarker={selectMarker}
                    cleanSelectedAsset={cleanSelectedAsset}
                />
            ) : null}
        </FeatureGroup>
    )
}

const CustomMarker = ({
    assetEvent,
    markerKey,
    areaKey,
    searchValue,
    highlightAsset,
    selectedAsset,
    isMobile,
    includeBoundingArea,
    selectMarker,
    cleanSelectedAsset
}: {
    assetEvent: MapWidgetEvent
    markerKey: React.Key
    areaKey: React.Key | undefined
    searchValue: String
    highlightAsset: String
    selectedAsset: SelectedAsset
    isMobile: String
    includeBoundingArea: boolean
    selectMarker: (
        assetName: string,
        animationType: 'panTo' | 'flyTo',
        includeBoundingArea: boolean,
        areaKey: React.Key,
        e?: L.LeafletMouseEvent
    ) => void
    cleanSelectedAsset: (cleanSearch?: boolean) => void
}): React.ReactElement => {
    const lat = parseFloat(assetEvent.lat)
    const long = parseFloat(assetEvent.long)

    if (_.isNaN(lat) || _.isNaN(long)) return

    const isFiltered = searchValue.length && assetEvent.asset.toLowerCase().includes(searchValue.toLowerCase())

    const isLowerOpacity = assetEvent.asset.toLowerCase() !== highlightAsset.toLowerCase()

    let elementzIndex = null

    if (highlightAsset.length && !isLowerOpacity) elementzIndex = 10500
    else if (isFiltered) elementzIndex = 10400

    return (
        <Marker
            key={markerKey}
            id={assetEvent.asset}
            icon={getMarkerIcon(
                assetEvent.asset_type,
                (!selectedAsset && isFiltered) || _.get(selectedAsset, 'name', null) === assetEvent.asset,
                assetEvent
            )}
            position={L.latLng(lat, long)}
            onMouseOver={(e: L.LeafletMouseEvent): void => {
                if (isMobile) {
                    selectMarker(assetEvent.asset, 'panTo', includeBoundingArea, areaKey, e)
                } else if (!selectedAsset) {
                    e.target.openPopup()
                }
            }}
            onMouseOut={(e: L.LeafletMouseEvent): void => {
                selectedAsset ? null : e.target.closePopup()
            }}
            onClick={(e: L.LeafletMouseEvent): void => {
                selectMarker(assetEvent.asset, 'panTo', includeBoundingArea, areaKey, e)
            }}
            opacity={highlightAsset.length > 0 && isLowerOpacity ? 0.5 : 1}
            zIndexOffset={elementzIndex}
        >
            <Tooltip event={assetEvent} onClose={cleanSelectedAsset} />
        </Marker>
    )
}

const controlButtonsStyles = (theme, isMobile) => {
    return css`
        .leaflet-bar {
            border-radius: 2px;
            border: none !important;
            margin-right: ${widgetPadding}px !important;

            a {
                font-size: 25px !important;
                width: ${isMobile ? mobileBtnSize : webBtnSize}px !important;
                height: ${isMobile ? mobileBtnSize : webBtnSize}px !important;
                display: flex;
                justify-content: center;
                align-items: center;
                color: #37a3ef;
                background-color: ${theme == 'light' ? 'white' : '#212a3e'};
                &:hover {
                    display: flex;
                    color: #37a3ef;
                    background-color: ${theme == 'light' ? ' #f4f4f4' : '#3b4b6d'};
                }
                &:first-child {
                    border-radius: 2px !important;
                    box-shadow: 0 0px 3px rgba(0, 0, 0, 0.65) !important;
                    border: none !important;
                }
                &:last-child {
                    border-radius: 2px !important;
                    margin-top: 3px;
                    box-shadow: 0 0px 3px rgba(0, 0, 0, 0.65) !important;
                }
            }
        }
    `
}
const controlScaleStyles = (theme, isMobile) => {
    return css`
        .leaflet-control-scale {
            margin-left: ${widgetPadding}px !important;
            margin-bottom: ${widgetPadding * 2} !important;
            &-line {
                background-color: ${theme == 'light' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(59, 75, 109, 0.5)'};
                color: ${theme == 'light' ? '#333' : '#fff'};
                border: 2px solid ${theme == 'light' ? '#777' : '#fff'};
                border-top: none;
                &:last-child {
                    border-top: 2px solid ${theme == 'light' ? '#777' : '#fff'} !important;
                }
            }
        }
    `
}

const attributionStyles = (theme, isMobile) => {
    return css`
        .leaflet-control-attribution {
            background: ${theme == 'light' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(59, 75, 109, 0.5)'} !important;
            color: ${theme == 'light' ? '#333' : '#fff'};
            a {
                color: #37a3ef;
            }
        }
    `
}

const HighOrderDivMap = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    z-index: 1;
    .leaflet-container {
        z-index: 700;
    }

    ${props => controlButtonsStyles(props.dashTheme, props.isMobile)}
    ${props => TooltipStyles(props.dashTheme)}
    ${props => markerStyles(props.dashTheme)}
    ${props => controlScaleStyles(props.dashTheme, props.isMobile)}
    ${props => attributionStyles(props.dashTheme, props.isMobile)}
`

const MapStyle = {
    width: '100%',
    height: '100%'
}
